import { CalendarOutlined } from '@ant-design/icons';
import { Flex, Typography } from 'antd';
import type { CSSProperties, ReactNode } from 'react';

export type TimestampCardItem = {
  label: string;
  value: string | Date | null | undefined;
};

function splitTimestamp(value: string | Date | null | undefined): {
  date: string;
  time: string;
} | null {
  if (value == null || value === '') {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return {
    date: date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }),
    time: date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    }),
  };
}

const CARD_STYLE: CSSProperties = {
  width: 168,
  padding: '12px 14px',
  borderRadius: 12,
  background: 'color-mix(in srgb, var(--evimo-blue) 5%, transparent)',
  border: '1px solid color-mix(in srgb, var(--evimo-blue) 14%, transparent)',
  textAlign: 'center',
};

function TimestampCard({
  label,
  value,
}: {
  label: string;
  value: string | Date | null | undefined;
}): ReactNode {
  const parts = splitTimestamp(value);

  return (
    <div style={CARD_STYLE}>
      <Flex justify="center" align="center" gap={6} style={{ marginBottom: 8 }}>
        <CalendarOutlined
          style={{ color: 'var(--evimo-blue)', fontSize: 13 }}
        />
        <Typography.Text
          type="secondary"
          style={{ fontSize: 11, lineHeight: 1.2, margin: 0 }}
        >
          {label}
        </Typography.Text>
      </Flex>
      {parts ? (
        <>
          <Typography.Text
            strong
            style={{
              display: 'block',
              fontSize: 14,
              lineHeight: 1.3,
              color: 'var(--evimo-text, #1a1d23)',
            }}
          >
            {parts.date}
          </Typography.Text>
          <Typography.Text
            type="secondary"
            style={{
              display: 'block',
              fontSize: 12,
              marginTop: 2,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {parts.time}
          </Typography.Text>
        </>
      ) : (
        <Typography.Text type="secondary" style={{ opacity: 0.55 }}>
          —
        </Typography.Text>
      )}
    </div>
  );
}

export function TimestampCards({
  items,
}: {
  items: TimestampCardItem[];
}): ReactNode {
  return (
    <Flex gap={8} justify="center" wrap="wrap" style={{ marginBottom: 24 }}>
      {items.map((item) => (
        <TimestampCard key={item.label} label={item.label} value={item.value} />
      ))}
    </Flex>
  );
}
